<template>
  <section class="form-list">
    <el-form class="form" ref="form" :model="form" :rules="rules" label-width="120px">
      <el-form-item label="所属学员" prop="student_id">
        <v-select-student v-model="form.student_id" @change="handleStudentChange" />
      </el-form-item>

      <el-form-item label="代金券类型" prop="voucher_type_id">
        <v-select-voucher-type
          v-model="form.voucher_type_id"
          :student-id="form.student_id"
          @change="handleVoucherTypeChange"
        />
      </el-form-item>

      <el-form-item v-if="form.voucher_type_id" label="领取限制">
        <el-tag size="small" :type="form.is_unique ? 'warning' : 'success'">
          {{ form.is_unique ? '每人限领一张' : '可重复拥有' }}
        </el-tag>
      </el-form-item>

      <el-form-item v-if="form.voucher_type_id" label="叠加限制">
        <el-tag size="small" :type="form.is_stackable ? 'success' : 'warning'">
          {{ form.is_stackable ? '可叠加使用' : '不可叠加' }}
        </el-tag>
      </el-form-item>

      <el-form-item label="面额(元)">
        <v-input-money :value="toYuan(form.amount_in_cents)" :disabled="true" />
      </el-form-item>

      <el-form-item label="有效期">
        <span v-if="form.expire_mode === 1 && form.fixed_expire_time">{{ form.fixed_expire_time | timeToYmdHi }} 过期</span>
        <span v-else>{{ form.valid_duration_seconds | formatDuration }}</span>
      </el-form-item>

      <el-form-item v-if="form.card_charge_types && form.card_charge_types.length" label="适用课卡套餐">
        <el-tag
          v-for="ct in form.card_charge_types"
          :key="ct.id"
          size="small"
          type="info"
          style="margin-right: 4px;"
        >{{ ct.card_charge_type_name }}</el-tag>
      </el-form-item>

      <el-form-item v-if="form.rule" label="使用规则">
        <span>{{ form.rule }}</span>
      </el-form-item>

      <el-form-item label="限制使用门店">
        <!-- 超级管理员可自由选择；其他人员只能在本门店与全国通用之间二选一 -->
        <v-select-store
          v-if="isAdministrator"
          class="w100"
          v-model="form.limit_store_id"
          placeholder="全国通用（不限制）"
          :clearable="true"
        />
        <template v-else>
          <el-checkbox v-model="form.limit_own_store">仅限本门店使用</el-checkbox>
          <div class="info-color store-limit-tip">取消勾选后，代金券全国通用（不受门店限制）</div>
        </template>
      </el-form-item>

      <el-form-item :label="applyRemarkLabel" prop="apply_remark">
        <el-input v-model="form.apply_remark" type="textarea" :rows="3" maxlength="250" show-word-limit />
      </el-form-item>

      <v-multi-file-upload v-model="form.remark_files" label="备注文件" prefix="voucher"/>

      <el-form-item>
        <el-button type="primary" :loading="isSubmitting" @click="handleSubmit">提交</el-button>
      </el-form-item>
    </el-form>
  </section>
</template>

<script>
import RouteConfig from '@/router/config';
import { mapGetters } from 'vuex';
import { toYuan } from '@/filters';
import { store as storeVoucher } from '@/api/voucher';
import { store as storeApply } from '@/api/voucher-add-apply';

export default {
  name: RouteConfig.voucherAdd.name,
  data: () => ({
    form: {
      student_id: null,
      voucher_type_id: null,
      voucher_type_name: '',
      valid_duration_seconds: 0,
      expire_mode: 0,
      fixed_expire_time: 0,
      amount_in_cents: 0,
      is_unique: 0,
      is_stackable: 0,
      limit_store_id: '',
      limit_own_store: true,// 非超级管理员默认仅限本门店使用，取消勾选即全国通用
      apply_remark: '',
      remark_files: [],
      rule: '',
      card_charge_types: [],
    },
    isSubmitting: false,
  }),
  computed: {
    ...mapGetters(['isAdministrator', 'userStoreId']),
    rules() {
      return {
        student_id: [{ required: true, message: '请选择学员', trigger: 'change' }],
        voucher_type_id: [{ required: true, message: '请选择代金券类型', trigger: 'change' }],
        apply_remark: [{ required: true, message: this.isAdministrator ? '请输入办理备注' : '请输入申请原因', trigger: 'blur' }],
      };
    },
    applyRemarkLabel() {
      return this.isAdministrator ? '办理备注' : '申请原因';
    },
  },
  methods: {
    toYuan,
    handleStudentChange() {
      // 换学员后清空已选类型与其派生信息：该学员「是否已拥有」会随学员变化
      this.form.voucher_type_id = null;
      this.form.voucher_type_name = '';
      this.form.valid_duration_seconds = 0;
      this.form.expire_mode = 0;
      this.form.fixed_expire_time = 0;
      this.form.amount_in_cents = 0;
      this.form.rule = '';
      this.form.card_charge_types = [];
      this.form.is_unique = 0;
      this.form.is_stackable = 0;
    },
    handleVoucherTypeChange(item) {
      if (item) {
        this.form.voucher_type_id = item.id;
        this.form.voucher_type_name = item.voucher_type_name || '';
        this.form.valid_duration_seconds = item.valid_duration_seconds || 0;
        this.form.expire_mode = item.expire_mode || 0;
        this.form.fixed_expire_time = item.fixed_expire_time || 0;
        this.form.amount_in_cents = item.amount_in_cents || 0;
        this.form.rule = item.rule || '';
        this.form.card_charge_types = item.card_charge_types || [];
        this.form.is_unique = item.is_unique ? 1 : 0;
        this.form.is_stackable = item.is_stackable ? 1 : 0;
      }
    },
    handleSubmit() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return;
        this.isSubmitting = true;

        const params = {
          student_id: this.form.student_id,
          voucher_type_id: this.form.voucher_type_id,
          voucher_type_name: this.form.voucher_type_name,
          valid_duration_seconds: this.form.valid_duration_seconds,
          amount_in_cents: this.form.amount_in_cents,
          // 非超级管理员：勾选仅限本门店则锁死本门店，取消勾选即全国通用（0）
          limit_store_id: this.isAdministrator
            ? (this.form.limit_store_id || 0)
            : (this.form.limit_own_store ? this.userStoreId : 0),
          apply_remark: this.form.apply_remark,
          remark_files: this.form.remark_files,
        };

        const apiCall = this.isAdministrator ? storeVoucher(params) : storeApply(params);
        const { isError } = await apiCall;
        this.isSubmitting = false;
        if (isError) return;

        this.$message.success(this.isAdministrator ? '办理成功' : '申请已提交');
        this.isAdministrator ? RouteConfig.voucherList.push() : RouteConfig.voucherAddApplyList.push();
      });
    },
  },
};
</script>

<style scoped>
.store-limit-tip {
  font-size: 12px;
  line-height: 20px;
  margin-top: 4px;
}
</style>
